import _ from 'lodash';
import {
  FETCH_RIGS,
  FETCH_RIG,
  DELETE_RIG,
  CREATE_RIG,
  CLEAR_RIGS,
} from './types';
import { setErrorMessage } from './errorMessage';
import api from '../apis/api';
import wait from '../util/wait';
import { fetchProvider } from './providers';
import getMessageFromApiError from '../util/getMessageFromApiError';

let rigs_list_generation = 0;

const captureRigsListGeneration = () => rigs_list_generation;

const bumpRigsListGeneration = () => {
  rigs_list_generation += 1;
  return rigs_list_generation;
};

const isCurrentRigsListGeneration = (list_generation) =>
  list_generation === rigs_list_generation;

export const fetchRigs = (provider_uuid, region_uuid) => async (dispatch) => {
  const list_generation = bumpRigsListGeneration();
  try {
    const res = await api.get(
      `/provider/${provider_uuid}/region/${region_uuid}/node`,
    );
    if (!isCurrentRigsListGeneration(list_generation)) {
      return;
    }
    dispatch({
      type: FETCH_RIGS,
      payload: res.data,
    });
  } catch (error) {
    const message = getMessageFromApiError(error);
    dispatch(setErrorMessage(message));
    throw new Error(`API Error - ${message}`);
  }
};

export const fetchRig =
  (provider_uuid, region_uuid, rig_uuid) => async (dispatch) => {
    const list_generation = captureRigsListGeneration();
    try {
      const res = await api.get(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}`,
      );
      if (!isCurrentRigsListGeneration(list_generation)) {
        return;
      }
      if (_.isEmpty(res.data)) {
        dispatch({ type: DELETE_RIG, payload: region_uuid });
      } else {
        dispatch({
          type: FETCH_RIG,
          payload: res.data,
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        if (!isCurrentRigsListGeneration(list_generation)) {
          return;
        }
        dispatch({ type: DELETE_RIG, payload: rig_uuid });
      } else {
        const message = getMessageFromApiError(error);
        dispatch(setErrorMessage(message));
        throw new Error(`API Error - ${message}`);
      }
    }
  };

export const updateRig =
  (provider_uuid, region_uuid, rig_uuid, data) => async (dispatch) => {
    const list_generation = captureRigsListGeneration();
    try {
      const res = await api.patch(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}`,
        data,
      );
      if (!isCurrentRigsListGeneration(list_generation)) {
        return;
      }
      dispatch({
        type: FETCH_RIG,
        payload: res.data,
      });
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const deleteRig =
  (provider_uuid, region_uuid, rig_uuid) => async (dispatch) => {
    try {
      const res = await api.delete(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}`,
      );

      dispatch({
        type: DELETE_RIG,
        payload: rig_uuid,
      });
    } catch (error) {
      if (error.response && error.response.status === 404) {
        dispatch({ type: DELETE_RIG, payload: rig_uuid });
      } else {
        const message = getMessageFromApiError(error);
        dispatch(setErrorMessage(message));
        throw new Error(`API Error - ${message}`);
      }
    }
  };

export const createRig =
  (provider_uuid, region_uuid, values) => async (dispatch) => {
    const list_generation = captureRigsListGeneration();
    try {
      const res = await api.post(
        `/provider/${provider_uuid}/region/${region_uuid}/node`,
        values,
      );
      if (!isCurrentRigsListGeneration(list_generation)) {
        return;
      }
      dispatch({
        type: CREATE_RIG,
        payload: res.data,
      });
      fetchProvider(provider_uuid);
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const toggleMaintenance =
  (provider_uuid, region_uuid, rig_uuid) => async (dispatch) => {
    const list_generation = captureRigsListGeneration();
    try {
      const res = await api.patch(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}/maintenance`,
      );
      if (!isCurrentRigsListGeneration(list_generation)) {
        return;
      }
      dispatch({
        type: FETCH_RIG,
        payload: res.data,
      });
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const reinstallNode =
  (provider_uuid, region_uuid, rig_uuid) => async (dispatch) => {
    const list_generation = captureRigsListGeneration();
    try {
      const res = await api.patch(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}/reinstall`,
      );
      if (!isCurrentRigsListGeneration(list_generation)) {
        return;
      }
      dispatch({
        type: FETCH_RIG,
        payload: res.data,
      });
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const replaceNode =
  (provider_uuid, region_uuid, rig_uuid, minion_id) => async (dispatch) => {
    const list_generation = captureRigsListGeneration();
    try {
      const res = await api.put(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}/replace`,
        { minion_id },
      );
      if (!isCurrentRigsListGeneration(list_generation)) {
        return;
      }
      dispatch({
        type: FETCH_RIG,
        payload: res.data,
      });
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const enableRemoteSupport =
  (provider_uuid, region_uuid, rig_uuid) => async (dispatch) => {
    const list_generation = captureRigsListGeneration();
    try {
      const res = await api.patch(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}/support/enable`,
      );
      if (!isCurrentRigsListGeneration(list_generation)) {
        return;
      }
      dispatch({
        type: FETCH_RIG,
        payload: res.data,
      });
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const disableRemoteSupport =
  (provider_uuid, region_uuid, rig_uuid) => async (dispatch) => {
    const list_generation = captureRigsListGeneration();
    try {
      const res = await api.patch(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}/support/disable`,
      );
      if (!isCurrentRigsListGeneration(list_generation)) {
        return;
      }
      dispatch({
        type: FETCH_RIG,
        payload: res.data,
      });
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const runAction =
  (provider_uuid, region_uuid, rig_uuid, command) => async (dispatch) => {
    try {
      await api.post(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}/action`,
        { command },
      );
      await wait(10000); // give the rig time to report and update
      dispatch(fetchRig(provider_uuid, region_uuid, rig_uuid));
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const deployDeviceEndpoint =
  (provider_uuid, region_uuid, rig_uuid) => async (dispatch) => {
    try {
      await api.put(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}/deploy`,
      );
      await wait(20000); // give the job time to start updating the rig
      await dispatch(fetchRig(provider_uuid, region_uuid, rig_uuid));
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const stopDeviceEndpoint =
  (provider_uuid, region_uuid, rig_uuid) => async (dispatch) => {
    try {
      await api.put(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}/stop`,
      );
      await dispatch(fetchRig(provider_uuid, region_uuid, rig_uuid));
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const getNodeLatestVersion =
  (provider_uuid, region_uuid) => async (dispatch) => {
    try {
      const res = await api.get(
        `/provider/${provider_uuid}/region/${region_uuid}/node/version/current`,
      );
      return res.data.version;
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const upgradeNode =
  (provider_uuid, region_uuid, rig_uuid) => async (dispatch) => {
    const list_generation = captureRigsListGeneration();
    try {
      const res = await api.put(
        `/provider/${provider_uuid}/region/${region_uuid}/node/${rig_uuid}/upgrade`,
        {},
      );
      if (!isCurrentRigsListGeneration(list_generation)) {
        return;
      }
      dispatch({
        type: FETCH_RIG,
        payload: res.data,
      });
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const clearRigs = () => (dispatch) => {
  bumpRigsListGeneration();
  dispatch({
    type: CLEAR_RIGS,
  });
};
